// CardGuarantee.jsx — public "save a card to hold your appointment" page.
// Owner directive 2026-08-31: facial appointments are held with a card on
// file — nothing charged at booking. Policy: free changes up to 24h before;
// $25 for changes within 24h (credited to the treatment when she attends the
// rescheduled visit within 14 days); $50 no-show.
//
// Reached from an SMS link sent by the front desk: /card?cid=<id>&n=<first>
// Reuses the proven membership card-save flow: stripe-membership
// create_setup_intent → Stripe PaymentElement → save_payment_method
// (persists to saved_payment_methods + flips clients.card_on_file).
import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "./supabase";

const stripePromise = loadStripe("pk_live_51TPSNN9gc0MT8Jg7aFesfPT0xlWbQ5fx1kHIpHqw1nwna52Z0sai0h5L0nHbQfO9kRII1On3Ag4ikZwO6zCJ9vuc00FIGFrxoh");

const C = {
  bg:"#f8f5f0", card:"#ffffff", border:"#e8e4df", gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)", red:"#c0392b",
  text:"#1a1a2e", muted:"#6b7280", light:"#f3f4f6",
};
const F = "'Montserrat','Helvetica Neue',Arial,sans-serif";
const S = "Georgia,'Times New Roman',serif";

function InnerForm({ clientId, onSaved, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!stripe || !elements) return;
    setBusy(true);
    onError("");
    try {
      const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
        elements, redirect: "if_required", confirmParams: {},
      });
      if (confirmErr) { onError(confirmErr.message || "Card validation failed"); setBusy(false); return; }
      const pmId = setupIntent?.payment_method;
      if (!pmId) { onError("No payment method returned — please try again"); setBusy(false); return; }
      const { data, error: saveErr } = await supabase.functions.invoke("stripe-membership", {
        body: { action: "save_payment_method", client_id: clientId, payment_method_id: pmId, set_default: true },
      });
      if (saveErr || data?.error) { onError(saveErr?.message || data?.error || "Could not save card"); setBusy(false); return; }
      onSaved();
    } catch (err) {
      onError(err?.message || String(err));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit}>
      <PaymentElement options={{ terms: { card: "never" } }} />
      <button type="submit" disabled={busy || !stripe} style={{ width:"100%",marginTop:16,padding:"14px",fontSize:15,fontWeight:700,background:busy?C.light:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:busy?C.muted:"#fff",border:"none",borderRadius:10,cursor:busy?"not-allowed":"pointer",fontFamily:F }}>
        {busy ? "Saving…" : "🔒 Save my card"}
      </button>
    </form>
  );
}

export default function CardGuarantee() {
  const params = new URLSearchParams(window.location.search);
  const clientId = Number(params.get("cid")) || null;
  const firstName = (params.get("n") || "").replace(/[^A-Za-z' -]/g, "").slice(0, 30);
  const [clientSecret, setClientSecret] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const start = async () => {
    setLoading(true); setErr("");
    try {
      const { data, error } = await supabase.functions.invoke("stripe-membership", {
        body: { action: "create_setup_intent", client_id: clientId },
      });
      if (error || data?.error) { setErr(error?.message || data?.error || "Could not load the card form"); return; }
      if (!data?.client_secret) { setErr("Could not load the card form — please try again"); return; }
      setClientSecret(data.client_secret);
    } catch (e) {
      setErr(e?.message || "Could not load the card form");
    } finally { setLoading(false); }
  };

  const policyRow = (a, b, strong) => (
    <div style={{ display:"flex",justifyContent:"space-between",gap:12,padding:"9px 0",borderBottom:`1px solid ${C.border}`,fontSize:13.5 }}>
      <span style={{ color:C.text }}>{a}</span>
      <span style={{ color:strong?C.red:C.green,fontWeight:700,whiteSpace:"nowrap" }}>{b}</span>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh",background:C.bg,fontFamily:F,padding:"24px 16px" }}>
      <div style={{ maxWidth:460,margin:"0 auto",background:C.card,borderRadius:16,overflow:"hidden",border:`1px solid ${C.border}`,boxShadow:"0 10px 40px rgba(0,0,0,0.08)" }}>
        <div style={{ background:"linear-gradient(135deg,#0a0a18,#1a1a2e)",padding:"26px 24px",textAlign:"center" }}>
          <div style={{ fontSize:12,letterSpacing:"0.25em",color:C.gold,fontWeight:600 }}>NOVA MED SPA</div>
          <div style={{ fontSize:22,color:"#fff",fontFamily:S,marginTop:6 }}>Reserve Your Spot</div>
        </div>
        <div style={{ padding:"24px" }}>
          {done ? (
            <div style={{ textAlign:"center",padding:"20px 0" }}>
              <div style={{ fontSize:44,marginBottom:10 }}>💛</div>
              <div style={{ fontSize:17,fontWeight:700,color:C.text,marginBottom:8 }}>You're all set{firstName ? `, ${firstName}` : ""}!</div>
              <div style={{ fontSize:13.5,color:C.muted,lineHeight:1.6 }}>Your card is safely on file and your appointment is reserved. Nothing was charged today. See you soon!</div>
            </div>
          ) : (
            <>
              <p style={{ fontSize:14.5,color:C.text,lineHeight:1.6,margin:"0 0 14px" }}>
                Hi{firstName ? ` ${firstName}` : ""}! 💛 To hold your appointment, please save a card on file — <b>you won't be charged today.</b>
              </p>
              <div style={{ background:"#faf7f2",borderRadius:10,padding:"6px 14px",marginBottom:18 }}>
                {policyRow("Cancel or change 24+ hours before", "Free", false)}
                {policyRow("Change within 24 hours", "$25 — credited to your treatment*", true)}
                {policyRow("No-show", "$50", true)}
                <div style={{ fontSize:11,color:C.muted,padding:"8px 0 6px" }}>*The $25 comes off your total when you attend your rescheduled visit within 14 days.</div>
              </div>
              {!clientId && <div style={{ padding:12,background:"#fdf2f0",borderRadius:8,color:C.red,fontSize:13 }}>This link is missing its booking details — please use the exact link we texted you, or call (516) 543-3940.</div>}
              {clientId && !clientSecret && (
                <button onClick={start} disabled={loading} style={{ width:"100%",padding:"14px",fontSize:15,fontWeight:700,background:loading?C.light:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:loading?C.muted:"#fff",border:"none",borderRadius:10,cursor:loading?"not-allowed":"pointer",fontFamily:F }}>
                  {loading ? "Opening secure form…" : "Add my card"}
                </button>
              )}
              {clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: { variables: { colorPrimary: C.gold } } }}>
                  <InnerForm clientId={clientId} onSaved={() => setDone(true)} onError={setErr} />
                </Elements>
              )}
              {err && <div style={{ marginTop:12,padding:12,background:"#fdf2f0",borderRadius:8,color:C.red,fontSize:13 }}>{err}</div>}
              <div style={{ marginTop:16,fontSize:11,color:C.muted,textAlign:"center" }}>🔒 Secured by Stripe · Nova Med Spa never sees your card number</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
